// FD-592 (FD-ADR-047 §4–§5) — the console's half of the AirBridge sign-in helper.
//
// The helper is a browser extension (`extension/`, FD-604). After the operator
// finishes the browser sign-in, Entra sends that tab to
// `http://localhost:<port>/?code=…&state=…`, which nothing serves. The extension
// sees that navigation and — only for a `state` this console registered — hands
// `{code, state}` back to this tab and closes the localhost one.
//
// Everything goes over the extension's RELAY (window.postMessage, protocol v1;
// `extension/README.md` is the contract). It needs no extension id, works on every
// trusted console, and it is the only channel the result arrives on anyway.
//
// ── THE CHECKS ON EVERY INCOMING MESSAGE ARE THE SECURITY CONTROL ──────────
//
// `window.postMessage` is a door any frame, any other window and any script on the
// page can knock on. A message is accepted only when ALL hold:
//
//   1. `event.source === window`      — posted by this page's own context (the
//                                       relay runs in it), never an iframe or an
//                                       opener;
//   2. `event.origin === location.origin`;
//   3. `channel === 'airbridge-signin'` and `direction === 'from-extension'` —
//                                       our own outgoing posts arrive on the same
//                                       listener and must not read as replies;
//   4. for a RESULT: `state` equals EXACTLY the state this page registered.
//
// A code with the wrong state is not "probably fine": it is a return from a
// different sign-in — another tab, a stale attempt, or a link someone else sent
// the minting account — and redeeming it would mint a credential for a sign-in
// this operator did not start. `parseSignInResult` is the one place that rule
// lives, and it is mutation-tested.
//
// ── NOTHING HERE PERSISTS ──────────────────────────────────────────────────
//
// `pendingStates` is a module-level Set: it dies with the page. That is what lets
// the console tell, after a RELOAD, that a returning sign-in belongs to a page
// that no longer holds its PKCE verifier — and say so rather than fail opaquely.

export const SIGN_IN_CHANNEL = 'airbridge-signin';
export const SIGN_IN_PROTOCOL = 1;

/**
 * How long a ping waits for the relay before calling the helper absent.
 *
 * FD-607: the extension is Manifest V3, so its service worker is suspended when
 * idle and has to WAKE before it can answer. 600 ms was short enough that a
 * sleeping helper read as absent. These are generous on purpose: a ping only
 * decides copy, and a register gates nothing but the order of two calls.
 */
export const HELPER_PING_TIMEOUT_MS = 1500;

/**
 * How long a register waits. It must complete BEFORE the sign-in tab opens (the
 * extension only forwards a state it already holds), so this is also the most
 * the operator waits on "Opening the Microsoft sign-in…" when no helper answers.
 */
export const HELPER_REGISTER_TIMEOUT_MS = 3000;

// The timeouts the defaults below actually use. Production never changes these;
// the test seam shortens them so a suite without a helper does not wait seconds
// per test. The constants above are what ships, and a test asserts them.
let pingTimeoutMs = HELPER_PING_TIMEOUT_MS;
let registerTimeoutMs = HELPER_REGISTER_TIMEOUT_MS;

/** Test seam: shorten the waits. Call with no arguments to restore the shipped values. */
export function setSignInHelperTimeoutsForTests(
  timeouts: { pingMs?: number; registerMs?: number } = {},
): void {
  pingTimeoutMs = timeouts.pingMs ?? HELPER_PING_TIMEOUT_MS;
  registerTimeoutMs = timeouts.registerMs ?? HELPER_REGISTER_TIMEOUT_MS;
}

export type HelperTrust = 'builtin' | 'managed' | 'approved';

export interface HelperPong {
  version: string | null;
  protocol: number | null;
  trust: HelperTrust | null;
}

export type SignInResult =
  | { kind: 'code'; state: string; code: string }
  | { kind: 'error'; state: string; error: string; errorDescription: string | null };

type Outgoing =
  | { type: 'airbridge.ping' }
  | { type: 'airbridge.register'; state: string }
  | { type: 'airbridge.unregister'; state: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Checks 1–3 above. `win` is injectable only so the checks are testable against a
 * foreign `source` without a second jsdom window.
 */
export function isFromHelper(event: MessageEvent, win: Window = window): boolean {
  if (event.source !== win) return false;
  if (event.origin !== win.location.origin) return false;
  const data = event.data as unknown;
  return isRecord(data) && data.channel === SIGN_IN_CHANNEL && data.direction === 'from-extension';
}

/**
 * Parse an `airbridge.result` for ONE expected state. Returns `null` for anything
 * that is not that exact sign-in's result — including a result for a different
 * state. Never throws.
 */
export function parseSignInResult(
  event: MessageEvent,
  expectedState: string,
  win: Window = window,
): SignInResult | null {
  if (!isFromHelper(event, win)) return null;
  const data = event.data as Record<string, unknown>;
  if (data.type !== 'airbridge.result') return null;
  if (typeof expectedState !== 'string' || expectedState.length === 0) return null;
  if (typeof data.state !== 'string' || data.state !== expectedState) return null;

  if (typeof data.code === 'string' && data.code.length > 0) {
    return { kind: 'code', state: data.state, code: data.code };
  }
  if (typeof data.error === 'string' && data.error.length > 0) {
    return {
      kind: 'error',
      state: data.state,
      error: data.error,
      errorDescription: typeof data.errorDescription === 'string' ? data.errorDescription : null,
    };
  }
  return null;
}

// ── pending states (memory only) ────────────────────────────────────────────

const pendingStates = new Set<string>();

export function markSignInPending(state: string): void {
  pendingStates.add(state);
}

export function clearSignInPending(state: string): void {
  pendingStates.delete(state);
}

export function isSignInPending(state: string): boolean {
  return pendingStates.has(state);
}

/** Test seam: the Set is module state and must not leak between tests. */
export function resetSignInPendingForTests(): void {
  pendingStates.clear();
}

// ── request / reply over the relay ──────────────────────────────────────────

function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Post one request and wait for its reply. Resolves `null` on timeout — a missing
 * helper and a helper not enabled on this origin look identical from here, and
 * both mean "use the paste box".
 */
function request(message: Outgoing, timeoutMs: number): Promise<Record<string, unknown> | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  const requestId = newRequestId();
  return new Promise((resolve) => {
    let done = false;
    const finish = (value: Record<string, unknown> | null) => {
      if (done) return;
      done = true;
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
      resolve(value);
    };
    const onMessage = (event: MessageEvent) => {
      if (!isFromHelper(event)) return;
      const data = event.data as Record<string, unknown>;
      if (data.requestId !== requestId) return;
      finish(isRecord(data.response) ? data.response : null);
    };
    const timer = window.setTimeout(() => finish(null), timeoutMs);
    window.addEventListener('message', onMessage);
    window.postMessage(
      { channel: SIGN_IN_CHANNEL, direction: 'to-extension', requestId, message },
      window.location.origin,
    );
  });
}

export async function pingSignInHelper(
  timeoutMs: number = pingTimeoutMs,
): Promise<HelperPong | null> {
  const response = await request({ type: 'airbridge.ping' }, timeoutMs);
  if (!response || response.ok !== true || response.type !== 'airbridge.pong') return null;
  const trust = response.trust;
  return {
    version: typeof response.version === 'string' ? response.version : null,
    protocol: typeof response.protocol === 'number' ? response.protocol : null,
    trust: trust === 'builtin' || trust === 'managed' || trust === 'approved' ? trust : null,
  };
}

/**
 * Register `state` with the helper. MUST run before the sign-in tab opens: the
 * extension only forwards a return whose state it already holds. `false` means
 * the helper is absent or refused (`untrusted_origin`, `state_in_use`, …) and the
 * paste box is the path.
 */
export async function registerSignInState(
  state: string,
  timeoutMs: number = registerTimeoutMs,
): Promise<boolean> {
  const response = await request({ type: 'airbridge.register', state }, timeoutMs);
  return Boolean(response && response.ok === true && response.type === 'airbridge.registered');
}

/** Best-effort withdrawal on cancel. Registrations also expire on their own. */
export function unregisterSignInState(state: string): void {
  void request({ type: 'airbridge.unregister', state }, registerTimeoutMs);
}

/**
 * Subscribe to the result for ONE state. Returns the unsubscribe function. The
 * callback fires at most once.
 */
export function listenForSignInResult(
  state: string,
  onResult: (result: SignInResult) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};
  let fired = false;
  const onMessage = (event: MessageEvent) => {
    if (fired) return;
    const result = parseSignInResult(event, state);
    if (!result) return;
    fired = true;
    window.removeEventListener('message', onMessage);
    onResult(result);
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

/**
 * Whether a message is a well-formed helper RESULT for a state this page is NOT
 * waiting on. After a reload that is exactly what a finishing sign-in looks like:
 * the extension still delivers to the tab, but the verifier died with the old page.
 * Used only to SAY so — an orphan result is never redeemed.
 */
export function isOrphanSignInResult(event: MessageEvent, win: Window = window): boolean {
  if (!isFromHelper(event, win)) return false;
  const data = event.data as Record<string, unknown>;
  if (data.type !== 'airbridge.result' || typeof data.state !== 'string') return false;
  return !pendingStates.has(data.state);
}

/** Whether a message is the relay's `airbridge.ready` announcement. */
export function isHelperReady(event: MessageEvent, win: Window = window): boolean {
  if (!isFromHelper(event, win)) return false;
  return (event.data as Record<string, unknown>).type === 'airbridge.ready';
}
